import React from 'react';
import { projects, techStack } from '../data/mock';
import { useInView } from '../hooks/useInView';
import { ArrowUpRight, Terminal, Database, Cloud, Server, Mail, GitBranch, Box, Cpu } from 'lucide-react';

const techIcons = {
  'Node.js': Terminal,
  'PostgreSQL': Database,
  'AWS': Cloud,
  'Kafka': Cpu,
  'Docker': Box,
  'AWS SES': Mail,
  'Redis': Database,
  'Git': GitBranch,
};

const ProjectCard = ({ project, index }) => {
  const [ref, isInView] = useInView({ threshold: 0.15 });

  return (
    <div
      ref={ref}
      className={`group relative transition-all duration-700 ease-out ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="p-8 md:p-12 rounded-3xl border border-gray-100 bg-white hover:border-gray-200 hover:shadow-[0_8px_40px_rgba(0,0,0,0.06)] transition-all duration-500 relative overflow-hidden">
        {/* Number watermark */}
        <span className="absolute top-4 right-8 font-heading text-[7rem] md:text-[9rem] font-[800] text-gray-50/70 leading-none select-none pointer-events-none">
          {String(index + 1).padStart(2, '0')}
        </span>

        <div className="relative z-10">
          <div className="flex items-start justify-between mb-8">
            <div>
              <p className="font-mono text-[11px] text-gray-300 uppercase tracking-widest mb-3">
                {project.tagline}
              </p>
              <h3 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
                {project.name}
              </h3>
            </div>
            <a
              href={project.link}
              className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center shrink-0 group-hover:bg-[#0a0a0a] group-hover:border-[#0a0a0a] group-hover:text-white text-gray-400 transition-all duration-400"
            >
              <ArrowUpRight size={18} className="transition-transform duration-400 group-hover:rotate-45" />
            </a>
          </div>

          <p className="text-gray-400 leading-relaxed max-w-xl mb-8 text-[15px]">
            {project.description}
          </p>

          <div className="flex flex-wrap gap-2">
            {project.tech.map((t) => (
              <span
                key={t}
                className="text-[11px] font-mono px-3 py-1.5 bg-[#fafafa] text-gray-500 rounded-full border border-gray-100"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectsSection = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.3 });
  const [techRef, techInView] = useInView({ threshold: 0.2 });

  return (
    <section id="projects" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`mb-16 transition-all duration-700 ${
            headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="font-mono text-[12px] text-gray-400 tracking-widest uppercase mb-4">
            Selected Work
          </p>
          <h2 className="font-heading text-4xl md:text-[3.25rem] font-[200] tracking-tight leading-tight">
            Projects<span className="text-gray-200">.</span>
          </h2>
        </div>

        {/* Project Cards */}
        <div className="space-y-8 mb-28">
          {projects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>

        {/* Tech Stack */}
        <div
          ref={techRef}
          className={`transition-all duration-700 ${
            techInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="font-mono text-[12px] text-gray-400 tracking-widest uppercase mb-8">
            Tech Stack
          </p>
          <div className="flex flex-wrap gap-3">
            {techStack.map((tech, index) => {
              const Icon = techIcons[tech.name] || Server;
              return (
                <div
                  key={tech.name}
                  className={`flex items-center gap-3 px-5 py-3 rounded-full border border-gray-100 bg-white hover:border-gray-300 hover:shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all duration-300 cursor-default ${
                    techInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${index * 60 + 200}ms` }}
                >
                  <Icon size={15} className="text-gray-400" strokeWidth={1.5} />
                  <span className="text-sm font-medium text-gray-600">{tech.name}</span>
                  <span className="text-[10px] font-mono text-gray-300 hidden sm:inline">{tech.category}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
